/**
 * hinterland.ts — the working landscape around each settlement (pure,
 * worker-safe): von Thünen field rings, the windmill ridge spot, the burial
 * ground, and shoreline quay segments.
 *
 * The Unbuilt Town wave. Real towns sit inside a visible economic landscape —
 * gardens, then field crops, then pasture, then wild land (von Thünen 1826).
 * Atlas simulates 19 commodities and farm jobs with zero landscape presence;
 * these functions give the simulation its ground. Everything derives from
 * (seed, site) + analytic terrain alone, so every client, worker and the
 * server-side cemetery mirror agree byte-for-byte.
 *
 * Layout: the agricultural band is an annulus OUTSIDE the town edge-works
 * ring (plan radius + wall margin), gridded into tangent-oriented cells.
 * Each cell rolls crop / orchard / pasture, and is rejected on slope, sea,
 * ports, the burial ground, the mill, and the regional route tracks.
 */
import { PLAN_RADIUS_DESIRED } from './settlement_plan.js';
import { SETTLEMENT_FOOTPRINT, settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { terrainHeight } from '../../../shared/terrain.js';
import { commonsSeaLevel } from './sea_level.js';
import { spaceportSpot, shuttleSpot, terminalSpot, arrivalPoint } from './ports.js';
import { capitalTheta0, capitalMainGate, boulevardAngles, CAPITAL_WALL_R } from '../../../shared/capitals.js';
import { distToRoute } from './regional_routes.js';
import { circuitDist } from './race_circuit.js';
import { CEMETERY_SPOTS } from '../../../shared/cemetery_spots.generated.js';

/** FNV-1a → [0,1) — the ports/settlement hash family (server-mirrorable). */
export function hs01(seed: number, salt: string): number {
  let h = 0x811c9dc5;
  const s = `${seed}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 0xffffffff;
}

export type FieldKind = 'crop' | 'orchard' | 'pasture';

export interface FieldCell {
  /** Cell center (world), tangent yaw, half extents (along/across the ring). */
  x: number; z: number; yaw: number; hw: number; hd: number;
  kind: FieldKind;
  /** 0..1 per-cell roll (crop phase, orchard spacing jitter, …). */
  roll: number;
}

/** The agricultural band, relative to the DESIRED plan radius (the wall sits
 *  at planRadius + 8; fields start outside it with a working margin). */
const BAND_IN_M = 26;
const BAND_OUT: Record<SettlementSite['tier'], number> = { city: 190, town: 150, hamlet: 110, capital: 320, district: 0, circuit: 0 };
const CELL_ALONG = 30;
const CELL_DEEP = 20;
const MAX_FIELD_SLOPE = 0.16;

interface SiteWorks {
  cells: FieldCell[];
  windmill: { x: number; z: number; yaw: number } | null;
  cemetery: { x: number; z: number; yaw: number; hw: number; hd: number } | null;
  quays: Array<{ x: number; z: number; yaw: number; len: number }>;
}

const worksCache = new Map<string, SiteWorks>();

function slopeAt(seed: string, x: number, z: number, r = 4): number {
  const dhx = Math.abs(terrainHeight(seed, x + r, z) - terrainHeight(seed, x - r, z));
  const dhz = Math.abs(terrainHeight(seed, x, z + r) - terrainHeight(seed, x, z - r));
  return Math.max(dhx, dhz) / (2 * r);
}

const sitesCache = new Map<string, SettlementSite[]>();
function sitesFor(seed: string): SettlementSite[] {
  let s = sitesCache.get(seed);
  if (!s) { s = settlementSites(seed); sitesCache.set(seed, s); }
  return s;
}

/** The precinct's half width (along the gate wall) and half depth per tier — the BASE size; the rows grow outward past it. */
export const CEMETERY_HALF: Record<SettlementSite['tier'], [number, number]> = { city: [20, 12], town: [16, 10], hamlet: [11, 8], capital: [30, 16], district: [0, 0], circuit: [12, 8] };

/** The burial ground. THE BAKED SPOT (2026-09-23): `CEMETERY_SPOTS` (bake:cemeteries —
 *  the plan's roads and gate exits, the regional routes, the ports, water, slope and the
 *  neighbours all judged on the client, where the plans live) is read first; the ring
 *  formula below is the fallback for a seed with no table. Frame: local +z points AWAY
 *  from the town (yaw = bearing − π/2 in the standard rotation the plots use), the gate
 *  on the −z side faces the town, rows grow outward. SERVER MIRROR: server/src/lib/cemetery.ts
 *  reads the same table and mirrors this math byte for byte — the server places every grave. */
export function cemeterySpot(seed: string, site: SettlementSite): { x: number; z: number; yaw: number; hw: number; hd: number } | null {
  // Capital wave: a district is a ward of its capital (never a settlement).
  if (site.tier === 'district') return null;
  const baked = CEMETERY_SPOTS[seed]?.[site.id];
  if (baked) return { x: baked[0], z: baked[1], yaw: baked[2], hw: baked[3], hd: baked[4] };
  const [hw, hd] = CEMETERY_HALF[site.tier];
  if (site.tier === 'capital') {
    // Capital wave (phase 3): the precinct stands outside the wall beside the
    // SECOND gate (the boulevard after the main one), off the exit road.
    const other = settlementSites(seed).find((o) => o.tier === 'capital' && o.id !== site.id);
    const th = capitalTheta0(site.siteSeed);
    const main = other ? capitalMainGate(th, other.centerX - site.centerX, other.centerZ - site.centerZ) : 0;
    const R = CAPITAL_WALL_R + 16 + hd + 8;
    const a = boulevardAngles(th)[(main + 1) % 6] + (Math.hypot(hw, hd) + 8 + 12) / R;
    return { x: site.centerX + Math.cos(a) * R, z: site.centerZ + Math.sin(a) * R, yaw: a - Math.PI / 2, hw, hd };
  }
  const R = PLAN_RADIUS_DESIRED[site.tier] + 14 + hd;
  const sea = commonsSeaLevel(seed);
  const sp = spaceportSpot(seed, site);
  const sh = shuttleSpot(seed, site);
  const arr = arrivalPoint(seed, site);
  const base = hs01(site.siteSeed, 'cemetery') * Math.PI * 2;
  let best: { x: number; z: number; yaw: number; score: number } | null = null;
  for (let k = 0; k < 24; k++) {
    const a = base + (k / 24) * Math.PI * 2;
    const x = site.centerX + Math.cos(a) * R;
    const z = site.centerZ + Math.sin(a) * R;
    let score = slopeAt(seed, x, z, 12) * 100;
    if (terrainHeight(seed, x, z) < sea + 1.5) continue;
    if (Math.hypot(x - arr.x, z - arr.z) < 60) score += 40;
    for (const p of [sp, sh]) {
      if (!p) continue;
      const px = site.centerX + p.x, pz = site.centerZ + p.z;
      const d = Math.hypot(x - px, z - pz);
      if (d < 120) score += (120 - d) * 0.5; // keep the quiet edge quiet
    }
    if (!best || score < best.score) best = { x, z, yaw: a - Math.PI / 2, score };
  }
  if (!best) return null;
  return { x: best.x, z: best.z, yaw: best.yaw, hw, hd };
}

/** Row pitch and column pitch of the plots (m), and the verge inside the wall. */
export const CEMETERY_COL_M = 2.6, CEMETERY_ROW_M = 3.2, CEMETERY_VERGE_M = 3;
/** Plots per row of a precinct. */
export function cemeteryCols(hw: number): number { return Math.max(4, Math.floor((hw * 2 - CEMETERY_VERGE_M * 2) / CEMETERY_COL_M)); }
/** The half depth a precinct needs to hold `graves` plots (never under its base `hd`) — the wall the dressing draws. */
export function cemeteryDepthFor(hd: number, hw: number, graves: number): number {
  const rows = Math.max(1, Math.ceil(graves / cemeteryCols(hw)));
  return Math.max(hd, (CEMETERY_VERGE_M * 2 + rows * CEMETERY_ROW_M) / 2);
}

/** Grave plot P (0-based) inside the precinct: ordered rows from the gate (the town side)
 *  OUTWARD, never wrapping — the old `% (COLS·ROWS)` stacked 2,312 graves on occupied spots.
 *  SERVER MIRROR — the server places new grave rows exactly here. */
export function cemeteryPlot(seed: string, site: SettlementSite, plotIndex: number): { x: number; z: number } | null {
  const c = cemeterySpot(seed, site);
  if (!c) return null;
  const COLS = cemeteryCols(c.hw);
  const col = plotIndex % COLS;
  const row = Math.floor(plotIndex / COLS);
  const lx = -c.hw + CEMETERY_VERGE_M + col * CEMETERY_COL_M + CEMETERY_COL_M / 2;
  const lz = -c.hd + CEMETERY_VERGE_M + row * CEMETERY_ROW_M + CEMETERY_ROW_M / 2;
  const cos = Math.cos(c.yaw), sin = Math.sin(c.yaw);
  return { x: c.x + lx * cos - lz * sin, z: c.z + lx * sin + lz * cos };
}

/** The windmill: the highest workable point on the outer ring (mills go where
 *  the resource is — ridge wind — not where the grid is convenient). */
export function windmillSpot(seed: string, site: SettlementSite): { x: number; z: number; yaw: number } | null {
  if (site.tier !== 'town' && site.tier !== 'hamlet') return null; // metros (and capitals) run fusion, not sails
  const R = PLAN_RADIUS_DESIRED[site.tier] + 34;
  const sea = commonsSeaLevel(seed);
  const cem = cemeterySpot(seed, site);
  const base = hs01(site.siteSeed, 'windmill') * Math.PI * 2;
  let best: { x: number; z: number; h: number } | null = null;
  for (let k = 0; k < 24; k++) {
    const a = base + (k / 24) * Math.PI * 2;
    const x = site.centerX + Math.cos(a) * R;
    const z = site.centerZ + Math.sin(a) * R;
    const h = terrainHeight(seed, x, z);
    if (h < sea + 1.5) continue;
    if (slopeAt(seed, x, z, 6) > 0.45) continue;
    if (cem && Math.hypot(x - cem.x, z - cem.z) < 50) continue;
    if (!best || h > best.h) best = { x, z, h };
  }
  if (!best) return null;
  return { x: best.x, z: best.z, yaw: hs01(site.siteSeed, 'windyaw') * Math.PI * 2 };
}

/** Shore quays: where the sea laps within the settlement's working reach, a
 *  boardwalk lands (waterfront settlements finally face their water). */
function findQuays(seed: string, site: SettlementSite): Array<{ x: number; z: number; yaw: number; len: number }> {
  if (site.tier === 'district' || site.tier === 'circuit') return []; // no working shore of their own
  const sea = commonsSeaLevel(seed);
  const out: Array<{ x: number; z: number; yaw: number; len: number }> = [];
  const R0 = SETTLEMENT_FOOTPRINT[site.tier] * 1.05;
  const R1 = PLAN_RADIUS_DESIRED[site.tier] + 60;
  for (let k = 0; k < 48 && out.length < 3; k++) {
    const a = (k / 48) * Math.PI * 2;
    // March outward looking for the waterline.
    for (let r = R0; r < R1; r += 12) {
      const x = site.centerX + Math.cos(a) * r;
      const z = site.centerZ + Math.sin(a) * r;
      if (terrainHeight(seed, x, z) >= sea - 0.2) continue;
      // Shore point: step back to the last dry sample.
      const sx = site.centerX + Math.cos(a) * (r - 12);
      const sz = site.centerZ + Math.sin(a) * (r - 12);
      if (terrainHeight(seed, sx, sz) < sea + 0.2) break; // already wet — cliffy
      if (out.some((q) => Math.hypot(q.x - sx, q.z - sz) < 90)) break;
      out.push({ x: sx, z: sz, yaw: a, len: 18 });
      break;
    }
  }
  return out;
}

function buildWorks(seed: string, site: SettlementSite): SiteWorks {
  const R = PLAN_RADIUS_DESIRED[site.tier];
  const sea = commonsSeaLevel(seed);
  const cemetery = cemeterySpot(seed, site);
  const windmill = windmillSpot(seed, site);
  const quays = findQuays(seed, site);
  // Ports keep the fields off (a capital's ports stand in its field band; a city's are inside its plan).
  const arr = arrivalPoint(seed, site);
  const ports = [spaceportSpot(seed, site), shuttleSpot(seed, site), terminalSpot(seed, site)]
    .filter((p): p is NonNullable<typeof p> => !!p)
    .map((p) => ({ x: site.centerX + p.x, z: site.centerZ + p.z }));
  ports.push({ x: arr.x, z: arr.z });
  const cells: FieldCell[] = [];
  const r0 = R + BAND_IN_M;
  const r1 = R + BAND_OUT[site.tier];
  // Neighbor pre-filter (perf): the full 312-site loop per CELL was ~190k
  // hypots per settlement — only sites whose guard band can reach ours matter.
  const nearSites = sitesFor(seed).filter((o) => o.id !== site.id
    && Math.hypot(o.centerX - site.centerX, o.centerZ - site.centerZ) < r1 + SETTLEMENT_FOOTPRINT[o.tier] + 80);
  const neighborNear = (x: number, z: number): boolean =>
    nearSites.some((o) => Math.hypot(o.centerX - x, o.centerZ - z) < SETTLEMENT_FOOTPRINT[o.tier] + 60);
  for (let r = r0 + CELL_DEEP / 2; r + CELL_DEEP / 2 <= r1; r += CELL_DEEP + 6) {
    const n = Math.max(6, Math.floor((Math.PI * 2 * r) / (CELL_ALONG + 10)));
    const phase = hs01(site.siteSeed, `ring${Math.round(r)}`);
    for (let i = 0; i < n; i++) {
      const a = ((i + phase) / n) * Math.PI * 2;
      const x = site.centerX + Math.cos(a) * r;
      const z = site.centerZ + Math.sin(a) * r;
      const roll = hs01(site.siteSeed, `cell${Math.round(r)}:${i}`);
      if (roll < 0.30) continue; // fallow gaps — fields cluster, never a solid donut
      if (terrainHeight(seed, x, z) < sea + 1.0) continue;
      if (slopeAt(seed, x, z, 8) > MAX_FIELD_SLOPE) continue;
      if (neighborNear(x, z)) continue;
      if (distToRoute(seed, x, z) < 26) continue;
      if ((circuitDist(seed, x, z)?.d ?? Infinity) < 40) continue; // the Grand Circuit's corridor
      if (cemetery && Math.hypot(x - cemetery.x, z - cemetery.z) < 46) continue;
      if (ports.some((p) => Math.hypot(x - p.x, z - p.z) < 70)) continue;
      if (windmill && Math.hypot(x - windmill.x, z - windmill.z) < 34) continue;
      if (quays.some((q) => Math.hypot(x - q.x, z - q.z) < 50)) continue;
      const kind: FieldKind = roll < 0.62 ? 'crop' : roll < 0.82 ? 'orchard' : 'pasture';
      cells.push({ x, z, yaw: a + Math.PI / 2, hw: CELL_ALONG / 2, hd: CELL_DEEP / 2, kind, roll });
    }
  }
  return { cells, windmill, cemetery, quays };
}

/** Everything the hinterland places for one settlement (memoized per site). */
export function hinterlandWorks(seed: string, site: SettlementSite): SiteWorks {
  const key = `${seed}|${site.siteSeed}`;
  let w = worksCache.get(key);
  if (!w) { w = buildWorks(seed, site); worksCache.set(key, w); }
  return w;
}

/**
 * Tilled-soil mask 0..1 at a world point (CROP cells only, feathered edge) —
 * baked into the terrain's `fieldW` vertex channel by the chunk worker, the
 * way roadW carries the worn tracks. Worker-safe, analytic, memoized.
 */
export function fieldMask(seed: string, wx: number, wz: number): number {
  for (const site of sitesFor(seed)) {
    const R1 = PLAN_RADIUS_DESIRED[site.tier] + BAND_OUT[site.tier] + 30;
    const dx = wx - site.centerX;
    const dz = wz - site.centerZ;
    const d2 = dx * dx + dz * dz;
    if (d2 > R1 * R1) continue;
    const R0 = PLAN_RADIUS_DESIRED[site.tier] + BAND_IN_M - 12;
    if (d2 < R0 * R0) continue;
    for (const c of hinterlandWorks(seed, site).cells) {
      if (c.kind !== 'crop') continue;
      const cx = wx - c.x, cz = wz - c.z;
      const rr = c.hw + c.hd + 4;
      if (cx * cx + cz * cz > rr * rr) continue;
      const cos = Math.cos(c.yaw), sin = Math.sin(c.yaw);
      const lx = Math.abs(cos * cx + sin * cz);
      const lz = Math.abs(-sin * cx + cos * cz);
      const sd = Math.max(lx - c.hw, lz - c.hd);
      if (sd < 2) {
        const t = Math.max(0, Math.min(1, sd / 2));
        return 1 - t * t * (3 - 2 * t); // inside: 1 → feathered to 0 over 2 m
      }
    }
  }
  return 0;
}
